import React, { useCallback, useMemo, useState } from 'react';
import { createContext } from '@shared/utils/createContext';
import type { GalleryLayout } from '@shared/photoGalleryLayout';

// In-memory, preview-only layout overrides for Photo Gallery components, set by PreviewListener when
// the admin postMessages a new value. Stay in app state — never written to Apollo cache — so we don't
// pollute BE-truth with preview-only values. In production guest viewing, no admin sends messages and
// `overrides` stays empty, so every gallery falls through to its saved layout.
//
// Keyed by component id because a single custom page can render multiple Photo Gallery components, and
// editing one must not re-layout the others in the preview.
type PhotoGalleryLayoutOverrideStore = Readonly<{
  overrides: Readonly<Record<string, GalleryLayout>>;
  // `setLayoutOverride(componentId, null)` clears that component's override (reverts to the saved layout).
  setLayoutOverride: (componentId: string, layout: GalleryLayout | null) => void;
}>;

const noop = () => {};

const DEFAULT_STORE: PhotoGalleryLayoutOverrideStore = {
  overrides: {},
  setLayoutOverride: noop,
};

const [Provider, useStore] = createContext<PhotoGalleryLayoutOverrideStore>({
  name: 'PhotoGalleryLayoutOverrideContext',
  // No-op default so consumers (the PhotoGallery widget) work even when the provider isn't mounted —
  // important for any guest render path that bypasses GuestSiteBody.
  strict: false,
  initialValue: DEFAULT_STORE,
});

export const usePhotoGalleryLayoutOverride = (): PhotoGalleryLayoutOverrideStore => useStore(false) ?? DEFAULT_STORE;

export const PhotoGalleryLayoutOverrideProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [overrides, setOverrides] = useState<Record<string, GalleryLayout>>({});

  const setLayoutOverride = useCallback((componentId: string, layout: GalleryLayout | null) => {
    setOverrides(prev => {
      if (layout === null) {
        if (!(componentId in prev)) return prev;
        const next = { ...prev };
        delete next[componentId];
        return next;
      }
      if (prev[componentId] === layout) return prev;
      return { ...prev, [componentId]: layout };
    });
  }, []);

  const value = useMemo(() => ({ overrides, setLayoutOverride }), [overrides, setLayoutOverride]);
  return <Provider value={value}>{children}</Provider>;
};
